import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Input, Button, Tag, Space } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '../api/client';
import { formatDate, formatPhone } from '../utils/formatters';
import { CHANNEL_COLORS, CHANNEL_LABELS } from '../utils/constants';

interface ClientListItem {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  channels: string[];
  orders_count: number;
  created_at: string;
}

interface PaginatedResponse {
  items: ClientListItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const fetchClients = async (page: number, perPage: number, search: string): Promise<PaginatedResponse> => {
  const params: Record<string, string | number> = { page, per_page: perPage };
  if (search) params.search = search;
  const resp = await apiClient.get<PaginatedResponse>('/admin/clients', { params });
  return resp.data;
};

export const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['clients', page, perPage, search],
    queryFn: () => fetchClients(page, perPage, search),
    staleTime: 30000,
  });

  const columns: ColumnsType<ClientListItem> = [
    {
      title: 'Клієнт',
      key: 'client',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{record.name}</span>
          {record.phone && (
            <span style={{ fontSize: 12, color: '#888' }}>{formatPhone(record.phone)}</span>
          )}
          {record.email && (
            <span style={{ fontSize: 12, color: '#888' }}>{record.email}</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Канали',
      dataIndex: 'channels',
      key: 'channels',
      width: 200,
      render: (channels: string[]) =>
        channels.length > 0
          ? channels.map((ch) => (
              <Tag key={ch} color={CHANNEL_COLORS[ch as keyof typeof CHANNEL_COLORS]}>
                {CHANNEL_LABELS[ch as keyof typeof CHANNEL_LABELS] || ch}
              </Tag>
            ))
          : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Замовлень',
      dataIndex: 'orders_count',
      key: 'orders_count',
      width: 110,
      align: 'center',
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>{count}</Tag>
      ),
    },
    {
      title: 'Зареєстрований',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<UserOutlined />}
          onClick={() => navigate(`/clients/${record.id}`)}
        >
          Профіль
        </Button>
      ),
    },
  ];

  return (
    <div>
      <h1>Клієнти</h1>

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Пошук по імені, телефону, email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={() => { setSearch(searchInput); setPage(1); }}
            style={{ width: 320 }}
            prefix={<SearchOutlined />}
            allowClear
            onClear={() => { setSearch(''); setSearchInput(''); setPage(1); }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => { setSearch(searchInput); setPage(1); }}
          >
            Знайти
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 800 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: data?.total || 0,
            onChange: (p) => setPage(p),
            showTotal: (total) => `Всього: ${total} клієнтів`,
          }}
        />
      </Card>
    </div>
  );
};

export default ClientList;
